import { EmptyState } from "@/components/widgets";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Award, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { PanelCard, scopeLockToast } from "@/components/dashboard/primitives";
import { TierBadge, MomDelta } from "./TierBadge";

/* Fifth standings board: Kia OEM CX scorecard (group-visible, drilldown scoped).
   Recognition lens from the OEM — never feeds complaint accountability. */
export const CxScorecardLeaderboard = ({ board, myOutletId, navigate }) => {
  if (!board) return null;
  const lensLabel = board.lens === "overall" ? "Monthly Achievement"
    : `${board.lens} Score`;
  return (
    <PanelCard
      title={<span className="flex items-center gap-1.5">
        <Award size={15} className="text-[hsl(var(--cx-gold))]" />
        Kia CX Scorecard — {board.month_label} · {lensLabel}
      </span>}
      right={board.climber ? (
        <span className="text-[0.68rem] text-[hsl(var(--status-green))] font-medium" data-testid="cx-lb-climber">
          {board.climber.line}
        </span>
      ) : null}
      testId="cx-leaderboard-panel"
      className="col-span-2 order-[5.4] lg:order-none lg:col-span-12">
      {board.rows.length === 0 ? (
        <EmptyState title="No OEM scorecards uploaded yet" />
      ) : (
        <div className="overflow-x-auto">
          <Table className="cx-table min-w-[640px]" data-testid="cx-leaderboard-table">
            <TableHeader>
              <TableRow>
                <TableHead className="pl-4">#</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">Score</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead className="text-right pr-4">MoM</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {board.rows.map((r) => {
                const locked = r.in_scope === false;
                return (
                  <TableRow key={r.outlet_id}
                    className={cn(locked ? "cursor-default" : "cursor-pointer",
                      r.rank === 1 && "bg-[hsl(var(--cx-gold)/0.10)]",
                      r.outlet_id === myOutletId && "bg-[hsl(var(--accent))] border-l-2 border-l-[hsl(var(--cx-gold))]")}
                    data-locked={locked ? "true" : undefined}
                    data-testid={`cx-lb-row-${r.code}`}
                    onClick={() => locked
                      ? scopeLockToast("Scorecard details for this location are visible to its own team and leadership.")
                      : navigate(`/cx-scorecards?outlet=${r.outlet_id}`)}>
                    <TableCell className="pl-4 tabular">{r.rank}</TableCell>
                    <TableCell>
                      <span className={cn("font-medium", locked && "text-muted-foreground")}>{r.name}</span>{" "}
                      <span className="text-xs text-muted-foreground">{r.code}</span>
                      {locked && <Lock size={11} className="inline ml-1.5 -mt-0.5 text-muted-foreground/70" aria-label="details locked" />}
                      {r.outlet_id === myOutletId && (
                        <Badge variant="outline" className="ml-1.5 text-[0.6rem] border-[hsl(var(--cx-gold))] text-[hsl(var(--cx-gold))]">You</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular font-semibold">
                      {r.score !== null && r.score !== undefined ? Math.round(r.score) : "—"}
                      {r.max && r.max !== 1000 ? <span className="text-[0.65rem] text-muted-foreground font-normal"> / {r.max}</span> : null}
                    </TableCell>
                    <TableCell><TierBadge tier={r.tier} testId={`cx-lb-tier-${r.code}`} /></TableCell>
                    <TableCell className="text-right pr-4"><MomDelta value={r.mom_delta} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <p className="px-5 pt-1.5 pb-2 text-[0.65rem] text-muted-foreground">
            OEM lens from Kia — group-wide standings; rows with <Lock size={10} className="inline -mt-0.5" /> open only for that location's team and leadership. Never affects complaint escalation.
          </p>
        </div>
      )}
    </PanelCard>
  );
};
